import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject } from 'rxjs';
import { environment } from 'environments/environment';
import { AuthenticationService } from 'app/authentication/service';

@Injectable({ providedIn: 'root' })
export class SocketService {
  private socket: Socket | null = null;
  private currentToken: string | null = null;
  private readonly SERVER_URL = environment.apiUrl;
  /** Messages waiting for socket `connect` (avoids duplicate `once('connect')` emits). */
  private readonly outboundQueue: any[] = [];

  /** Streams `newMessage` from the active socket across reconnects. */
  private readonly inboundNewMessage$ = new Subject<any>();

  constructor(private readonly auth: AuthenticationService) {}

  connectWithToken(token: string): void {
    if (!token) {
      return;
    }
    const shouldReconnect = !this.socket || this.currentToken !== token;
    if (shouldReconnect) {
      this.disconnect();
      this.currentToken = token;
      this.socket = io(this.SERVER_URL, {
        transports: ['websocket'],
        auth: { token },
        autoConnect: false,
      });
      this.attachSocketConsumers(this.socket);
    }
    if (this.socket && !this.socket.connected) {
      this.socket.connect();
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.currentToken = null;
    this.outboundQueue.length = 0;
  }

  // ---- Emitters ----
  openConversation(senderId: string, partnerId: string): Promise<any> {
    return new Promise((resolve) => {
      if (!this.socket) {
        resolve(null);
        return;
      }
      this.socket.emit('openConversation', { sender: senderId, partnerId }, (response) => {
        resolve(response);
      });
    });
  }

  joinConversation(conversationId: string) {
    this.socket?.emit('joinConversation', conversationId);
  }

  /**
   * Emit sendMessage. Ensures a JWT connection; if the socket is still handshaking,
   * messages are queued and flushed on `connect`.
   */
  sendMessage(data: any) {
    const token = this.resolveAuthToken();
    if (!token) {
      console.error('[Socket] sendMessage: no JWT in session');
      return;
    }

    this.connectWithToken(token);

    const sock = this.socket;
    if (!sock) {
      return;
    }

    if (sock.connected) {
      sock.emit('sendMessage', data);
      return;
    }

    this.outboundQueue.push(data);
  }

  private attachSocketConsumers(sock: Socket) {
    sock.off('connect');
    sock.off('newMessage');
    sock.on('connect', () => this.flushOutboundQueue(sock));
    sock.on('newMessage', (msg: any) => this.inboundNewMessage$.next(msg));
  }

  private flushOutboundQueue(sock: Socket) {
    while (this.outboundQueue.length > 0 && sock.connected) {
      const payload = this.outboundQueue.shift();
      sock.emit('sendMessage', payload);
    }
  }

  private resolveAuthToken(): string | null {
    const user = this.auth.currentUserValue as any;
    if (!user) {
      return null;
    }
    return (user.partnerToken || user.accessToken || user.storeToken || '') as string;
  }

  deleteMessage(messageId: string) {
    this.socket?.emit('deleteMessage', messageId);
  }

  // ---- Listeners ----
  onNewMessage(): Observable<any> {
    return this.inboundNewMessage$.asObservable();
  }

  onConversationOpened(): Observable<any> {
    return new Observable((observer) => {
      if (!this.socket) {
        observer.complete();
        return;
      }
      const handler = (conversation) => observer.next(conversation);
      this.socket.on('conversationOpened', handler);
      return () => this.socket?.off('conversationOpened', handler);
    });
  }

  onMessageDeleted(): Observable<any> {
    return new Observable((observer) => {
      if (!this.socket) {
        observer.complete();
        return;
      }
      const handler = (id) => observer.next(id);
      this.socket.on('messageDeleted', handler);
      return () => this.socket?.off('messageDeleted', handler);
    });
  }

  // ---- Partner Conversations ----
  getPartnerConversations(partnerId: string) {
    this.socket?.emit('getPartnerConversations', partnerId);
  }

  onPartnerConversations(): Observable<any> {
    return new Observable((observer) => {
      if (!this.socket) {
        observer.complete();
        return;
      }
      const handler = (conversations) => observer.next(conversations);
      this.socket.on('partnerConversations', handler);
      return () => this.socket?.off('partnerConversations', handler);
    });
  }

  get isConnected(): boolean {
    return Boolean(this.socket?.connected);
  }
}
